import { fail, note, warn } from "@shared/lib/journal";
import {
  acquireRestartHandoff,
  currentRestartBlockers,
  prepareForRestart,
  type RestartBlocker,
} from "@shared/lib/desktop";
import { telemetryEvent } from "@shared/telemetry";
import { show as showNotification } from "@shared/lib/notify";
import {
  desktopUpdaterBridge,
  type PreparedUpdate,
  type InstallProgress,
  type UpdateBootMode,
  type UpdateMetadata,
  type UpdateRecoveryStatus,
  type UpdaterBridge,
  type UpdateTrigger,
} from "./bridge";

const STARTUP_BUDGET_MS = 2_500;
const BOOT_MODE_TIMEOUT_MS = 3_000;
const BOOT_MESSAGE_DELAY_MS = 350;
const PERIOD_MIN_MS = 4 * 60 * 60 * 1_000;
const PERIOD_JITTER_MS = 2 * 60 * 60 * 1_000;
const FOCUS_TTL_MS = 60 * 60 * 1_000;
const ONLINE_TTL_MS = 5 * 60 * 1_000;
const ONLINE_RETRY_DELAY_MS = 6_000;
const PREPARE_TIMEOUT_MS = 15_000;
const INSTALL_ATTEMPT_KEY = "airel.updater-install-attempt.v1";
const STARTUP_FAILURE_KEY = "airel.updater-startup-failure.v1";
const STORAGE_PROBE_KEY = "airel.updater-storage-probe.v1";

export type UpdatePhase =
  | "idle"
  | "checking"
  | "available"
  | "revalidating"
  | "downloading"
  | "verifying"
  | "staging"
  | "preparing"
  | "installing"
  | "restarting";

export type UpdateLaunchMode = "detecting" | "normal" | "healthProbe" | "failed";

export interface UpdateSnapshot {
  desktop: boolean;
  launchMode: UpdateLaunchMode;
  healthProbe: Extract<UpdateBootMode, { mode: "healthProbe" }> | null;
  healthProbeError: string | null;
  recovery: UpdateRecoveryStatus | null;
  recoveryActionError: string | null;
  booting: boolean;
  showBootMessage: boolean;
  phase: UpdatePhase;
  update: UpdateMetadata | null;
  downloaded: number;
  contentLength: number | null;
  dialog: boolean;
  installError: string | null;
  blockingMessage: string | null;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface InstallAttempt {
  schema: 1;
  currentVersion: string;
  targetVersion: string;
  startedAt: number;
  trigger: UpdateTrigger;
  reported: boolean;
}

interface StartupFailure {
  schema: 1;
  targetVersion: string;
  failedAt: number;
  errorKind: string;
}

interface ControllerOptions {
  bridge?: UpdaterBridge;
  now?: () => number;
  random?: () => number;
  setTimer?: typeof setTimeout;
  clearTimer?: typeof clearTimeout;
  telemetry?: typeof telemetryEvent;
  showNotification?: typeof showNotification;
  storage?: StorageLike | null;
  currentVersion?: string;
}

class RestartBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RestartBlockedError";
  }
}

function errorKind(error: unknown): string {
  if (error && typeof error === "object" && "code" in error && typeof error.code === "string") {
    return error.code;
  }
  if (error instanceof Error) return error.name || "Error";
  return typeof error;
}

function errorCode(error: unknown): string | null {
  if (error && typeof error === "object" && "code" in error && typeof error.code === "string") {
    return error.code;
  }
  return null;
}

function errorRetryable(error: unknown): boolean | null {
  if (error && typeof error === "object" && "retryable" in error && typeof error.retryable === "boolean") {
    return error.retryable;
  }
  return null;
}

function userError(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (error && typeof error === "object" && "message" in error && typeof error.message === "string") {
    return error.message;
  }
  if (typeof error === "string" && error.trim()) return error;
  return "Не удалось установить обновление";
}

function storageOrNull(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function parseAttempt(raw: string | null): InstallAttempt | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<InstallAttempt>;
    if (
      value.schema !== 1 ||
      typeof value.currentVersion !== "string" ||
      !value.currentVersion ||
      typeof value.targetVersion !== "string" ||
      !value.targetVersion ||
      typeof value.startedAt !== "number" ||
      !Number.isFinite(value.startedAt) ||
      !["startup", "periodic", "focus", "online", "manual"].includes(value.trigger ?? "")
    ) {
      return null;
    }
    return {
      schema: 1,
      currentVersion: value.currentVersion,
      targetVersion: value.targetVersion,
      startedAt: value.startedAt,
      trigger: value.trigger as UpdateTrigger,
      reported: value.reported === true,
    };
  } catch {
    return null;
  }
}

function parseStartupFailure(raw: string | null): StartupFailure | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<StartupFailure>;
    if (
      value.schema !== 1 ||
      typeof value.targetVersion !== "string" ||
      !value.targetVersion ||
      typeof value.failedAt !== "number" ||
      !Number.isFinite(value.failedAt) ||
      typeof value.errorKind !== "string" ||
      !value.errorKind
    ) {
      return null;
    }
    return {
      schema: 1,
      targetVersion: value.targetVersion,
      failedAt: value.failedAt,
      errorKind: value.errorKind,
    };
  } catch {
    return null;
  }
}

function sameVersion(left: string, right: string): boolean {
  const normalize = (value: string) => value.trim().replace(/^v/i, "");
  return normalize(left) === normalize(right);
}

interface SemanticVersion {
  core: [number, number, number];
  prerelease: string[] | null;
}

function parseSemanticVersion(value: string): SemanticVersion | null {
  const match = /^v?(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z.-]+))?(?:\+[0-9A-Za-z.-]+)?$/.exec(
    value.trim(),
  );
  if (!match) return null;

  const core = match.slice(1, 4).map(Number) as [number, number, number];
  if (!core.every(Number.isSafeInteger)) return null;

  const prerelease = match[4]?.split(".") ?? null;
  if (prerelease?.some((part) => !part || (/^\d+$/.test(part) && part.length > 1 && part.startsWith("0")))) {
    return null;
  }
  return { core, prerelease };
}

function compareSemanticVersion(left: SemanticVersion, right: SemanticVersion): number {
  for (let index = 0; index < left.core.length; index += 1) {
    const difference = left.core[index] - right.core[index];
    if (difference !== 0) return difference;
  }

  if (!left.prerelease && !right.prerelease) return 0;
  if (!left.prerelease) return 1;
  if (!right.prerelease) return -1;

  const length = Math.max(left.prerelease.length, right.prerelease.length);
  for (let index = 0; index < length; index += 1) {
    const leftPart = left.prerelease[index];
    const rightPart = right.prerelease[index];
    if (leftPart === undefined) return -1;
    if (rightPart === undefined) return 1;
    if (leftPart === rightPart) continue;

    const leftNumeric = /^\d+$/.test(leftPart);
    const rightNumeric = /^\d+$/.test(rightPart);
    if (leftNumeric && rightNumeric) {
      if (leftPart.length !== rightPart.length) return leftPart.length - rightPart.length;
      return leftPart < rightPart ? -1 : 1;
    }
    if (leftNumeric) return -1;
    if (rightNumeric) return 1;
    return leftPart < rightPart ? -1 : 1;
  }
  return 0;
}

/** Exact fallback keeps local/dev labels from being mistaken for a successful install. */
function versionAtLeast(current: string, target: string): boolean {
  if (sameVersion(current, target)) return true;
  const parsedCurrent = parseSemanticVersion(current);
  const parsedTarget = parseSemanticVersion(target);
  return !!parsedCurrent && !!parsedTarget && compareSemanticVersion(parsedCurrent, parsedTarget) >= 0;
}

function interruptedMessage(attempt: InstallAttempt): string {
  return `Предыдущая установка Airel ${attempt.targetVersion} не завершилась. Запустите её ещё раз`;
}

function startupFailureMessage(failure: StartupFailure): string {
  return `Автоматическое обновление Airel ${failure.targetVersion} не завершилось. Нажмите «Повторить», чтобы попробовать снова`;
}

export function recoveryMessage(recovery: UpdateRecoveryStatus): string {
  switch (recovery.status) {
    case "canceled":
      return `Установка Airel ${recovery.targetVersion} была отменена в системном окне. Текущая версия продолжает работать`;
    case "rolledBack":
      return `Обновление Airel ${recovery.targetVersion} не установилось, поэтому Windows вернула предыдущую рабочую версию`;
    case "recoveryPending":
      return `Airel пока не смог подтвердить результат установки ${recovery.targetVersion}. Не запускайте обновление повторно`;
  }
}

export function updateBusy(phase: UpdatePhase): boolean {
  return ["revalidating", "downloading", "verifying", "staging", "preparing", "installing", "restarting"].includes(phase);
}

export class UpdaterController {
  private readonly bridge: UpdaterBridge;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly setTimer: typeof setTimeout;
  private readonly clearTimer: typeof clearTimeout;
  private readonly telemetry: typeof telemetryEvent;
  private readonly showNotification: typeof showNotification;
  private readonly storage: StorageLike | null;
  private currentVersion: string;
  private readonly currentVersionOverride: boolean;
  private readonly listeners = new Set<() => void>();
  private readonly notifiedVersions = new Set<string>();
  private snapshot: UpdateSnapshot;
  private previousAttempt: InstallAttempt | null;
  private previousStartupFailure: StartupFailure | null;
  private started = false;
  private attemptReconciled = false;
  private nativeAttemptReadable = true;
  private startupWindow = true;
  private lastCheckedAt = Number.NEGATIVE_INFINITY;
  private checkFlight: Promise<UpdateMetadata | null> | null = null;
  private installFlight: Promise<void> | null = null;
  private runtimeFlight: Promise<void> | null = null;
  private prepareFlight: Promise<void> | null = null;
  private bootModeFlight: Promise<UpdateLaunchMode> | null = null;
  private healthProbeFlight: Promise<void> | null = null;
  private recoveryDismissFlight: Promise<void> | null = null;
  /** На macOS пакет уже может быть заменён, даже если IPC перезапуска оборвался. */
  private restartPending = false;

  constructor(options: ControllerOptions = {}) {
    this.bridge = options.bridge ?? desktopUpdaterBridge;
    this.now = options.now ?? Date.now;
    this.random = options.random ?? Math.random;
    // WebView timer functions require the Window receiver. Keeping an unbound
    // reference and later calling it as a controller member throws
    // `TypeError: Illegal invocation` in production builds.
    this.setTimer = options.setTimer ?? globalThis.setTimeout.bind(globalThis);
    this.clearTimer = options.clearTimer ?? globalThis.clearTimeout.bind(globalThis);
    this.telemetry = options.telemetry ?? telemetryEvent;
    this.showNotification = options.showNotification ?? showNotification;
    this.storage = options.storage === undefined ? storageOrNull() : options.storage;
    this.currentVersionOverride = options.currentVersion !== undefined;
    this.currentVersion = options.currentVersion ?? (import.meta.env.VITE_APP_VERSION || "local");
    this.previousAttempt = this.readAttempt();
    this.previousStartupFailure = this.readStartupFailure();

    const desktop = this.bridge.supported();
    this.snapshot = {
      desktop,
      launchMode: desktop ? "detecting" : "normal",
      healthProbe: null,
      healthProbeError: null,
      recovery: null,
      recoveryActionError: null,
      booting: desktop,
      showBootMessage: false,
      phase: desktop ? "checking" : "idle",
      update: null,
      downloaded: 0,
      contentLength: null,
      dialog: false,
      installError: null,
      blockingMessage: null,
    };
  }

  getSnapshot = (): UpdateSnapshot => this.snapshot;

  getCurrentVersion = (): string => this.currentVersion;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private set(next: Partial<UpdateSnapshot>) {
    this.snapshot = { ...this.snapshot, ...next };
    for (const listener of this.listeners) listener();
  }

  private event(name: string, attributes: Record<string, string> = {}) {
    this.telemetry(`updater.${name}`, attributes);
  }

  private readAttempt(): InstallAttempt | null {
    try {
      const attempt = parseAttempt(this.storage?.getItem(INSTALL_ATTEMPT_KEY) ?? null);
      if (!attempt) this.storage?.removeItem(INSTALL_ATTEMPT_KEY);
      return attempt;
    } catch {
      return null;
    }
  }

  private saveAttempt(attempt: InstallAttempt | null): boolean {
    this.previousAttempt = attempt;
    try {
      if (attempt) {
        const serialized = JSON.stringify(attempt);
        this.storage?.setItem(INSTALL_ATTEMPT_KEY, serialized);
        return this.storage?.getItem(INSTALL_ATTEMPT_KEY) === serialized;
      }
      this.storage?.removeItem(INSTALL_ATTEMPT_KEY);
      return this.storage?.getItem(INSTALL_ATTEMPT_KEY) === null;
    } catch {
      // Недоступный localStorage не должен мешать самой установке.
      return false;
    }
  }

  private readStartupFailure(): StartupFailure | null {
    try {
      const failure = parseStartupFailure(this.storage?.getItem(STARTUP_FAILURE_KEY) ?? null);
      if (!failure) this.storage?.removeItem(STARTUP_FAILURE_KEY);
      return failure;
    } catch {
      return null;
    }
  }

  private saveStartupFailure(failure: StartupFailure | null): void {
    this.previousStartupFailure = failure;
    try {
      if (failure) this.storage?.setItem(STARTUP_FAILURE_KEY, JSON.stringify(failure));
      else this.storage?.removeItem(STARTUP_FAILURE_KEY);
    } catch {
      // Недоступный localStorage не должен мешать запуску приложения.
    }
  }

  private startupFailureFor(update: UpdateMetadata): StartupFailure | null {
    const failure = this.previousStartupFailure;
    if (!failure) return null;
    if (sameVersion(failure.targetVersion, update.version)) return failure;
    this.saveStartupFailure(null);
    return null;
  }

  private existingUpdateError(update: UpdateMetadata): string | null {
    if (
      this.snapshot.recovery &&
      sameVersion(this.snapshot.recovery.targetVersion, update.version)
    ) {
      return recoveryMessage(this.snapshot.recovery);
    }
    if (this.previousAttempt) return interruptedMessage(this.previousAttempt);
    const failure = this.startupFailureFor(update);
    return failure ? startupFailureMessage(failure) : null;
  }

  private canPersistAutomaticAttempt(update: UpdateMetadata): boolean {
    const probe = JSON.stringify({
      schema: 1,
      currentVersion: update.currentVersion,
      targetVersion: update.version,
      startedAt: this.now(),
      trigger: "startup",
      reported: false,
    } satisfies InstallAttempt);
    try {
      this.storage?.setItem(STORAGE_PROBE_KEY, probe);
      const exact = this.storage?.getItem(STORAGE_PROBE_KEY) === probe;
      this.storage?.removeItem(STORAGE_PROBE_KEY);
      return exact;
    } catch {
      try {
        this.storage?.removeItem(STORAGE_PROBE_KEY);
      } catch {
        // Storage уже недоступен; startup всё равно перейдёт в ручной режим.
      }
      return false;
    }
  }

  private reconcileAttempt(): void {
    if (this.attemptReconciled) return;
    this.attemptReconciled = true;
    const attempt = this.previousAttempt;
    if (!attempt) return;

    if (versionAtLeast(this.currentVersion, attempt.targetVersion)) {
      note("обновл", "предыдущая установка завершилась успешно", {
        версия: this.currentVersion,
      });
      this.event("install_succeeded", {
        trigger: attempt.trigger,
        previous_version: attempt.currentVersion,
        target_version: attempt.targetVersion,
        current_version: this.currentVersion,
      });
      this.saveAttempt(null);
      this.saveStartupFailure(null);
      return;
    }

    warn("обновл", "предыдущая установка не завершилась", {
      ожидалась: attempt.targetVersion,
      запущена: this.currentVersion,
    });
    if (!attempt.reported) {
      this.event("install_incomplete", {
        trigger: attempt.trigger,
        previous_version: attempt.currentVersion,
        target_version: attempt.targetVersion,
        current_version: this.currentVersion,
      });
      this.saveAttempt({ ...attempt, reported: true });
    }
  }

  private async loadNativeAttempt(): Promise<void> {
    try {
      const native = await this.bridge.previousAttempt();
      if (!native) return;

      const existing = this.previousAttempt;
      const attempt: InstallAttempt = {
        schema: 1,
        currentVersion: native.currentVersion,
        targetVersion: native.targetVersion,
        startedAt: native.startedAt,
        trigger: native.trigger === "resume" ? "focus" : native.trigger,
        reported:
          !!existing &&
          sameVersion(existing.targetVersion, native.targetVersion) &&
          existing.reported,
      };
      this.saveAttempt(attempt);
      this.attemptReconciled = false;
      this.reconcileAttempt();
    } catch (error) {
      this.nativeAttemptReadable = false;
      warn("обновл", "не удалось проверить состояние прошлой установки", {
        ошибка: errorKind(error),
      });
      this.event("attempt_state_failed", { error_kind: errorKind(error) });
    }
  }

  private async loadRecoveryStatus(): Promise<void> {
    try {
      const recovery = await this.bridge.getRecoveryStatus();
      this.set({ recovery, recoveryActionError: null });
      if (!recovery) return;

      // Native recovery is the source of truth. The local marker only keeps a
      // dismissed terminal notification from turning into a startup auto-loop
      // for the exact same release on a later launch.
      this.saveStartupFailure({
        schema: 1,
        targetVersion: recovery.targetVersion,
        failedAt: recovery.occurredAt,
        errorKind: `update_${recovery.status}`,
      });
      this.event(`recovery_${recovery.status}`, {
        target_version: recovery.targetVersion,
        retryable: String(recovery.retryable),
        reason: recovery.reason,
      });
    } catch (error) {
      this.nativeAttemptReadable = false;
      warn("обновл", "не удалось проверить состояние восстановления", {
        ошибка: errorKind(error),
      });
      this.event("recovery_state_failed", { error_kind: errorKind(error) });
    }
  }

  completeHealthProbe(): Promise<void> {
    if (this.healthProbeFlight) return this.healthProbeFlight;
    const probe = this.snapshot.healthProbe;
    if (!probe) return Promise.reject(new Error("Проверка запуска обновления не активна"));

    this.healthProbeFlight = this.bridge
      .completeHealthProbe(probe.transactionId)
      .catch((error) => {
        fail("обновл", "не удалось подтвердить запуск новой версии", error, {
          версия: probe.targetVersion,
        });
        this.event("health_probe_failed", {
          target_version: probe.targetVersion,
          error_kind: errorKind(error),
        });
        this.set({
          launchMode: "failed",
          healthProbeError:
            "Airel не смог подтвердить запуск новой версии. Полностью закройте приложение и откройте его снова",
        });
        throw error;
      })
      .finally(() => {
        this.healthProbeFlight = null;
      });
    return this.healthProbeFlight;
  }

  detectBootMode(): Promise<UpdateLaunchMode> {
    if (!this.snapshot.desktop) return Promise.resolve("normal");
    if (this.snapshot.launchMode !== "detecting") {
      return Promise.resolve(this.snapshot.launchMode);
    }
    if (this.bootModeFlight) return this.bootModeFlight;

    let timeout: ReturnType<typeof setTimeout> | null = null;
    const deadline = new Promise<UpdateBootMode>((_, reject) => {
      timeout = this.setTimer(
        () => reject(new Error("Native boot mode не ответил вовремя")),
        BOOT_MODE_TIMEOUT_MS,
      );
    });

    this.bootModeFlight = Promise.race([this.bridge.getBootMode(), deadline])
      .then((mode) => {
        // The installed native package is authoritative. Build-time metadata is
        // only a web/dev fallback and may be absent in a manual Tauri build.
        if (!this.currentVersionOverride) this.currentVersion = mode.currentVersion;
        if (mode.mode === "healthProbe") {
          this.set({
            launchMode: "healthProbe",
            healthProbe: mode,
            booting: true,
            showBootMessage: true,
          });
          this.event("health_probe_started", { target_version: mode.targetVersion });
          return "healthProbe" as const;
        }

        this.set({ launchMode: "normal" });
        return "normal" as const;
      })
      .catch((error) => {
        fail("обновл", "не удалось определить безопасный режим запуска", error);
        this.event("boot_mode_failed", { error_kind: errorKind(error) });
        this.set({
          launchMode: "failed",
          booting: true,
          showBootMessage: true,
          healthProbeError:
            "Airel не смог проверить состояние обновления. Полностью закройте приложение и откройте его снова",
        });
        return "failed" as const;
      })
      .finally(() => {
        if (timeout) this.clearTimer(timeout);
        this.bootModeFlight = null;
      });
    return this.bootModeFlight;
  }

  start(): void {
    if (this.started || !this.snapshot.desktop) return;
    this.started = true;

    void this.startDesktop();
  }

  private async startDesktop(): Promise<void> {
    if ((await this.detectBootMode()) !== "normal") return;
    this.reconcileAttempt();

    const messageTimer = this.setTimer(() => {
      if (this.snapshot.booting) this.set({ showBootMessage: true });
    }, BOOT_MESSAGE_DELAY_MS);

    this.setTimer(() => {
      this.startupWindow = false;
      if (this.snapshot.booting) {
        this.set({
          booting: false,
          phase: updateBusy(this.snapshot.phase)
            ? this.snapshot.phase
            : this.snapshot.update
              ? "available"
              : "idle",
        });
      }
    }, STARTUP_BUDGET_MS);

    void this.loadRecoveryStatus()
      .then(() => this.loadNativeAttempt())
      .then(() => this.check("startup", 0))
      .then(async (update) => {
        if (!update) {
          this.set({ booting: false, phase: "idle" });
          return;
        }

        const existingError = this.nativeAttemptReadable
          ? this.existingUpdateError(update)
          : "Airel не смог проверить результат прошлой установки. Запустите обновление вручную";
        if (!this.startupWindow || existingError || this.snapshot.recovery) {
          this.set({
            booting: false,
            phase: "available",
            update,
            installError: existingError,
            blockingMessage: null,
          });
          return;
        }

        if (!this.canPersistAutomaticAttempt(update)) {
          warn("обновл", "автоматическая установка пропущена: состояние нельзя сохранить", {
            версия: update.version,
          });
          this.event("auto_install_skipped", {
            trigger: "startup",
            target_version: update.version,
            reason: "storage_unavailable",
          });
          this.set({ booting: false, phase: "available", update });
          return;
        }

        await this.install("startup");
      })
      .catch(() => {
        // Все ветки уже записали полезную причину. Startup всегда fail-open.
        this.set({ booting: false, phase: this.snapshot.update ? "available" : "idle" });
      })
      .finally(() => this.clearTimer(messageTimer));

    // Таймер остаётся активным во время revalidate/download: это общий бюджет
    // стартового экрана, а не только таймаут первого запроса манифеста.
  }

  async check(trigger: UpdateTrigger, ttl = 0): Promise<UpdateMetadata | null> {
    // После успешного apply пакет уже заменил приложение. До перезапуска нельзя
    // позволять фоновому check стереть update и durable attempt marker.
    if (
      !this.snapshot.desktop ||
      (this.started && this.snapshot.launchMode !== "normal") ||
      this.installFlight ||
      this.restartPending
    ) {
      return this.snapshot.update;
    }
    if (ttl > 0 && this.now() - this.lastCheckedAt < ttl) return this.snapshot.update;
    if (this.checkFlight) return this.checkFlight;

    if (!this.snapshot.booting && !this.snapshot.update) this.set({ phase: "checking" });
    note("обновл", "проверяю версию", { причина: trigger });
    this.event("check_started", { trigger });

    this.checkFlight = this.bridge
      .check(trigger)
      .then((update) => {
        this.lastCheckedAt = this.now();
        this.acceptCheckResult(update, trigger);
        return update;
      })
      .catch((error) => {
        warn("обновл", "проверка не удалась — продолжаю с установленной версией", {
          причина: trigger,
          ошибка: errorKind(error),
        });
        this.event("check_failed", { trigger, error_kind: errorKind(error) });
        if (!this.snapshot.booting) this.set({ phase: this.snapshot.update ? "available" : "idle" });
        throw error;
      })
      .finally(() => {
        this.checkFlight = null;
      });

    return this.checkFlight;
  }

  private acceptCheckResult(update: UpdateMetadata | null, trigger: UpdateTrigger): void {
    if (!update) {
      note("обновл", "установлена свежая версия", { причина: trigger });
      this.event("check_succeeded", { trigger, available: "false" });
      // A 204 can mean that a staged rollout was reduced or stopped after an
      // MSI/UAC failure. Keep durable failure markers until a later launch is
      // actually on the target version; otherwise offering the same release
      // again could silently restart the automatic install loop.
      this.set({
        update: null,
        phase: "idle",
        dialog: false,
        installError: null,
        blockingMessage: null,
      });
      return;
    }

    note("обновл", "найдена новая версия", {
      текущая: update.currentVersion,
      новая: update.version,
      причина: trigger,
    });
    this.event("available", {
      trigger,
      current_version: update.currentVersion,
      target_version: update.version,
    });
    this.notifyAboutBackgroundUpdate(update, trigger);
    this.set({
      update,
      phase: "available",
      installError: this.existingUpdateError(update),
      blockingMessage: null,
      dialog: this.snapshot.dialog,
    });
  }

  private notifyAboutBackgroundUpdate(update: UpdateMetadata, trigger: UpdateTrigger): void {
    if (
      (trigger !== "periodic" && trigger !== "online") ||
      typeof document === "undefined" ||
      document.visibilityState === "visible" ||
      sameVersion(this.snapshot.update?.version ?? "", update.version) ||
      this.notifiedVersions.has(update.version)
    ) {
      return;
    }

    this.notifiedVersions.add(update.version);
    // show() только читает уже выданное системное разрешение и никогда не
    // открывает prompt. Отказ здесь не мешает самому updater.
    void this.showNotification(
      "Доступно обновление Airel",
      `Версия ${update.version} готова к установке.`,
    );
  }

  openDialog(): void {
    if (this.snapshot.update || this.snapshot.recovery) this.set({ dialog: true });
  }

  closeDialog(): void {
    if (!updateBusy(this.snapshot.phase)) this.set({ dialog: false });
  }

  dismissRecovery(): Promise<void> {
    if (this.recoveryDismissFlight) return this.recoveryDismissFlight;
    const recovery = this.snapshot.recovery;
    if (!recovery || recovery.status === "recoveryPending") return Promise.resolve();

    this.recoveryDismissFlight = this.bridge
      .dismissRecovery(recovery.transactionId)
      .then(() => {
        this.event("recovery_dismissed", {
          target_version: recovery.targetVersion,
          status: recovery.status,
        });
        this.set({ recovery: null, recoveryActionError: null, dialog: false });
        this.set({
          installError: this.snapshot.update ? this.existingUpdateError(this.snapshot.update) : null,
        });
      })
      .catch((error) => {
        fail("обновл", "не удалось скрыть результат восстановления", error, {
          версия: recovery.targetVersion,
        });
        this.set({
          dialog: true,
          recoveryActionError: "Не удалось сохранить выбор. Повторите ещё раз",
        });
        throw error;
      })
      .finally(() => {
        this.recoveryDismissFlight = null;
      });
    return this.recoveryDismissFlight;
  }

  installRuntime(): Promise<void> {
    if (this.runtimeFlight) return this.runtimeFlight;
    if (this.started && this.snapshot.launchMode !== "normal") {
      return Promise.reject(new Error("Обновление нельзя запускать в служебном режиме"));
    }

    const initialBlockers = currentRestartBlockers();
    const approvedBlockers = new Map(
      initialBlockers.map((blocker) => [blocker.id, blocker.prepare] as const),
    );
    this.runtimeFlight = this.installRuntimeInner(
      approvedBlockers,
      initialBlockers.some((blocker) => blocker.kind === "wait"),
    ).finally(() => {
      this.runtimeFlight = null;
    });
    return this.runtimeFlight;
  }

  private async installRuntimeInner(
    approvedBlockers: Map<string, RestartBlocker["prepare"]>,
    downloadOnly: boolean,
  ): Promise<void> {
    if (this.restartPending) return this.restartRuntime();

    const recovery = this.snapshot.recovery;
    if (recovery?.status === "recoveryPending") {
      const blocked = new RestartBlockedError(
        "Результат прошлой установки ещё не подтверждён. Полностью перезапустите Airel; если сообщение останется, обратитесь в поддержку",
      );
      this.set({ dialog: true, blockingMessage: blocked.message });
      throw blocked;
    }
    if (recovery && !recovery.retryable) {
      const blocked = new RestartBlockedError(
        "Эту установку нельзя безопасно повторить. Скройте сообщение и дождитесь следующей версии или обратитесь в поддержку",
      );
      this.set({ dialog: true, blockingMessage: blocked.message });
      throw blocked;
    }
    if (recovery) await this.dismissRecovery();

    return this.install("manual", approvedBlockers, downloadOnly);
  }

  private install(
    trigger: UpdateTrigger,
    approvedBlockers = new Map<string, RestartBlocker["prepare"]>(),
    downloadOnly = false,
  ): Promise<void> {
    if (this.installFlight) return this.installFlight;
    const selectedUpdate = this.snapshot.update;
    if (!selectedUpdate) return Promise.resolve();

    this.set({
      phase: "revalidating",
      // Runtime download идёт полосой под titlebar и не перекрывает звонок.
      // Модальный этап начнётся только с подготовки к перезапуску.
      dialog: false,
      downloaded: 0,
      contentLength: null,
      installError: null,
      blockingMessage: null,
    });
    note("обновл", "готовлю безопасное обновление", {
      версия: selectedUpdate.version,
      причина: trigger,
    });
    this.event("install_started", { trigger, target_version: selectedUpdate.version });

    this.installFlight = this.installInner(trigger, selectedUpdate, approvedBlockers, downloadOnly)
      .catch(async (error) => {
        await this.handleInstallError(error, trigger, selectedUpdate);
        throw error;
      })
      .finally(() => {
        this.installFlight = null;
      });

    return this.installFlight;
  }

  private async installInner(
    trigger: UpdateTrigger,
    selectedUpdate: UpdateMetadata,
    approvedBlockers: Map<string, RestartBlocker["prepare"]>,
    downloadOnly: boolean,
  ): Promise<void> {
    const expectedVersion = selectedUpdate.version;
    let releaseRestartHandoff: (() => void) | null = null;
    let prepared: PreparedUpdate | null = null;
    let committed = false;
    let selectedBlockers: RestartBlocker[] = [];

    try {
      // Никаких разрушительных действий до сети, полной загрузки и minisign.
      const confirmed = await this.bridge.revalidate(expectedVersion, trigger);
      this.set({ update: confirmed, phase: "downloading" });
      await this.bridge.download(expectedVersion, (event) => this.onProgress(event));
      this.set({ phase: "verifying" });

      // Релиз могли отозвать или заменить, пока грузился большой пакет. Эта
      // проверка выдаёт native право подготовить транзакцию, но ещё не закрывает
      // звонок, загрузки или основное окно.
      const staged = await this.bridge.revalidate(expectedVersion, trigger);
      this.set({ update: staged });

      // Once the main UI is visible, even an install that began during startup
      // must respect newly-created calls, uploads and dirty state. It also needs
      // an explicit second click: startup consent expires with the startup gate.
      if (trigger !== "startup" || !this.snapshot.booting) {
        selectedBlockers = currentRestartBlockers();
        if (downloadOnly || trigger === "startup") {
          throw new RestartBlockedError(
            selectedBlockers.some((blocker) => blocker.kind === "wait")
              ? "Сначала завершите текущие действия"
              : trigger === "startup" && selectedBlockers.some((blocker) => blocker.kind === "confirm")
                ? "Пока загружалось обновление, началось действие, которое нужно отдельно завершить"
              : "Обновление загружено и проверено. Нажмите ещё раз, когда будете готовы перезапустить Airel",
          );
        }
        const unapproved = selectedBlockers.filter(
          (blocker) =>
            blocker.kind === "confirm" &&
            (!approvedBlockers.has(blocker.id) || approvedBlockers.get(blocker.id) !== blocker.prepare),
        );
        if (unapproved.length > 0) {
          throw new RestartBlockedError(
            "Пока загружалось обновление, началось действие, которое нужно отдельно завершить",
          );
        }
        this.set({ phase: "staging", dialog: true });
        prepared = await this.bridge.prepare(expectedVersion, trigger);
        if (!sameVersion(prepared.targetVersion, expectedVersion)) {
          throw {
            code: "update_changed",
            message: "Подготовлена другая версия обновления. Проверьте обновления ещё раз",
            retryable: false,
          };
        }
        this.set({ phase: "preparing", dialog: true });
        this.event("prepare_started", { trigger, target_version: staged.version });
        releaseRestartHandoff = await this.prepareRuntimeRestart(selectedBlockers);
      } else {
        this.set({ phase: "staging" });
        prepared = await this.bridge.prepare(expectedVersion, trigger);
        if (!sameVersion(prepared.targetVersion, expectedVersion)) {
          throw {
            code: "update_changed",
            message: "Подготовлена другая версия обновления. Проверьте обновления ещё раз",
            retryable: false,
          };
        }
        if (!this.snapshot.booting) {
          throw new RestartBlockedError(
            "Обновление подготовлено. Нажмите ещё раз, когда будете готовы перезапустить Airel",
          );
        }
        // Даже на стартовом экране новый API-запрос не должен попасть между
        // последней проверкой и передачей управления MSI.
        releaseRestartHandoff = acquireRestartHandoff();
        if (currentRestartBlockers().length > 0) {
          releaseRestartHandoff();
          releaseRestartHandoff = null;
          throw new RestartBlockedError("Пока Airel готовился, началось действие, которое нельзя прерывать");
        }
      }

      // Runtime preparation может занять до 15 секунд. Релиз проверяется ещё
      // раз уже под restart handoff, а затем снимок blockers проверяется после
      // сетевого ожидания. Отозванный или заменённый MSI никогда не коммитится.
      this.set({ phase: "revalidating" });
      const final = await this.bridge.revalidate(expectedVersion, trigger);
      this.set({ update: final });
      if (trigger === "startup" && !this.snapshot.booting) {
        throw new RestartBlockedError(
          "Обновление подготовлено. Нажмите ещё раз, когда будете готовы перезапустить Airel",
        );
      }
      const selectedById = new Map(
        selectedBlockers.map((blocker) => [blocker.id, blocker.prepare] as const),
      );
      const appeared = currentRestartBlockers().filter(
        (blocker) =>
          blocker.kind === "wait" ||
          !selectedById.has(blocker.id) ||
          selectedById.get(blocker.id) !== blocker.prepare,
      );
      if (appeared.length > 0) {
        throw new RestartBlockedError(
          "Пока Airel проверял обновление, началось действие, которое нельзя прерывать",
        );
      }

      // На Windows успешный вызов завершит WebView до продолжения Promise.
      // Метка позволяет следующему запуску отличить успех от отмены UAC/MSI.
      const attemptDurable = this.recordAttempt(final, trigger);
      if (trigger === "startup" && !attemptDurable) {
        throw new RestartBlockedError(
          "Airel не смог сохранить состояние установки. Запустите обновление вручную, чтобы избежать повторного запуска установщика",
        );
      }
      this.set({ phase: "installing" });
      this.event("apply_started", { trigger, target_version: final.version });
      try {
        await this.bridge.apply(expectedVersion, trigger, prepared.transactionId);
        committed = true;
      } catch (error) {
        // После SupervisorReady native уже владеет восстановлением. В других
        // pre-handoff ошибках локальная метка не должна изображать запущенный MSI.
        if (errorCode(error) !== "update_handoff_started") this.saveAttempt(null);
        throw error;
      }

      // На Windows успешный apply завершает процесс раньше этой строки. Там
      // suppression снимет reconciliation уже в новой версии приложения.
      this.saveStartupFailure(null);

      this.restartPending = true;
      this.set({ phase: "restarting" });
      note("обновл", "обновление установлено, перезапускаю", { версия: final.version });
      this.event("restart_requested", { trigger, target_version: final.version });
      await this.bridge.restart();
    } finally {
      if (prepared && !committed) {
        try {
          await this.bridge.abort(prepared.transactionId);
          this.event("prepared_update_aborted", { target_version: expectedVersion });
        } catch (abortError) {
          // После native handoff abort закономерно запрещён. Исходную причину
          // установки не маскируем; supervisor уже отвечает за recovery.
          warn("обновл", "не удалось отменить подготовленную транзакцию", {
            версия: expectedVersion,
            ошибка: errorKind(abortError),
          });
        }
      }
      // В production успешный MSI handoff/restart завершает процесс раньше.
      // Возврат сюда означает, что WebView продолжает жить (в том числе в
      // тесте), поэтому новые действия снова безопасно разрешать.
      releaseRestartHandoff?.();
    }
  }

  private recordAttempt(update: UpdateMetadata, trigger: UpdateTrigger): boolean {
    return this.saveAttempt({
      schema: 1,
      currentVersion: update.currentVersion,
      targetVersion: update.version,
      startedAt: this.now(),
      trigger,
      reported: false,
    });
  }

  private async handleInstallError(
    error: unknown,
    trigger: UpdateTrigger,
    selectedUpdate: UpdateMetadata,
  ): Promise<void> {
    if (error instanceof RestartBlockedError) {
      note("обновл", "установка ждёт завершения действий", {
        версия: selectedUpdate.version,
      });
      this.event("install_blocked", { trigger, target_version: selectedUpdate.version });
      this.set({
        booting: false,
        phase: "available",
        dialog: true,
        installError: null,
        blockingMessage: error.message,
      });
      return;
    }

    const code = errorCode(error);
    if (code === "update_restart_required") {
      this.restartPending = true;
      note("обновл", "пакет уже установлен и ждёт перезапуска", {
        версия: selectedUpdate.version,
      });
      this.event("restart_required", { trigger, target_version: selectedUpdate.version });
      this.set({
        booting: false,
        phase: "available",
        dialog: trigger !== "startup",
        installError: "Обновление установлено. Перезапустите Airel, чтобы закончить",
      });
      return;
    }

    if (!this.restartPending && code !== "update_changed" && code !== "update_withdrawn") {
      this.saveStartupFailure({
        schema: 1,
        targetVersion: selectedUpdate.version,
        failedAt: this.now(),
        errorKind: errorKind(error),
      });
    }

    if (
      code === "update_changed" ||
      code === "update_withdrawn" ||
      code === "no_pending_update" ||
      errorRetryable(error) === false
    ) {
      await this.refreshAfterInvalidation(trigger, code);
    }

    const restarting = this.restartPending;
    fail("обновл", restarting ? "не удалось перезапустить после установки" : "установка не удалась", error, {
      версия: selectedUpdate.version,
      причина: trigger,
    });
    this.event(restarting ? "restart_failed" : "install_failed", {
      trigger,
      target_version: selectedUpdate.version,
      error_kind: errorKind(error),
    });

    const changed = code === "update_changed";
    this.set({
      booting: false,
      phase: this.snapshot.update ? "available" : "idle",
      dialog: trigger !== "startup" && !!this.snapshot.update,
      installError: this.snapshot.update
        ? restarting
          ? "Обновление установлено, но Airel не смог перезапуститься. Перезапустите ещё раз"
          : changed
            ? "Релиз изменился во время загрузки. Проверьте новую версию и повторите"
            : userError(error)
        : null,
      blockingMessage: null,
    });
  }

  private async refreshAfterInvalidation(trigger: UpdateTrigger, code: string | null): Promise<void> {
    try {
      const fresh = await this.bridge.check(trigger);
      this.lastCheckedAt = this.now();
      this.acceptCheckResult(fresh, trigger);
    } catch (refreshError) {
      warn("обновл", "не удалось обновить сведения о релизе после отказа", {
        причина: trigger,
        ошибка: errorKind(refreshError),
      });
      if (code === "update_withdrawn" || code === "update_configuration_error") {
        this.set({ update: null, dialog: false, phase: "idle", blockingMessage: null });
      }
    }
  }

  private prepare(selected: RestartBlocker[]): Promise<void> {
    if (!this.prepareFlight) {
      const timeout = new RestartBlockedError(
        "Airel не дождался завершения текущего действия. Проверьте связь и повторите обновление",
      );
      const controller = new AbortController();
      let timer: ReturnType<typeof setTimeout> | null = null;
      const deadline = new Promise<void>((_, reject) => {
        timer = this.setTimer(() => {
          controller.abort(timeout);
          reject(timeout);
        }, PREPARE_TIMEOUT_MS);
      });

      this.prepareFlight = Promise.race([
        prepareForRestart(selected, controller.signal),
        deadline,
      ]).finally(() => {
        if (timer) this.clearTimer(timer);
        this.prepareFlight = null;
      });
    }
    return this.prepareFlight;
  }

  private async prepareRuntimeRestart(selected: RestartBlocker[]): Promise<() => void> {
    const waits = selected.filter((blocker) => blocker.kind === "wait");
    if (waits.length > 0) throw new RestartBlockedError("Сначала завершите текущие действия");

    await this.prepare(selected);

    const releaseRestartHandoff = acquireRestartHandoff();
    const selectedById = new Map(selected.map((blocker) => [blocker.id, blocker.prepare] as const));
    const appeared = currentRestartBlockers().filter(
      (blocker) =>
        blocker.kind === "wait" ||
        !selectedById.has(blocker.id) ||
        selectedById.get(blocker.id) !== blocker.prepare,
    );
    if (appeared.length > 0) {
      releaseRestartHandoff();
      throw new RestartBlockedError("Пока Airel готовился, началось действие, которое нельзя прерывать");
    }
    return releaseRestartHandoff;
  }

  private async restartRuntime(): Promise<void> {
    const update = this.snapshot.update;
    let releaseRestartHandoff: (() => void) | null = null;
    try {
      const selected = currentRestartBlockers();
      this.set({ phase: "preparing", dialog: true, installError: null, blockingMessage: null });
      this.event("prepare_started", {
        trigger: "manual",
        target_version: update?.version ?? "unknown",
      });
      releaseRestartHandoff = await this.prepareRuntimeRestart(selected);
      this.set({ phase: "restarting" });
      note("обновл", "повторяю перезапуск после установленного обновления", {
        версия: update?.version ?? "неизвестно",
      });
      this.event("restart_requested", {
        trigger: "manual",
        target_version: update?.version ?? "unknown",
      });
      await this.bridge.restart();
    } catch (error) {
      if (error instanceof RestartBlockedError) {
        this.set({ phase: "available", dialog: true, installError: null, blockingMessage: error.message });
        throw error;
      }
      fail("обновл", "не удалось перезапустить после установки", error, {
        версия: update?.version ?? "неизвестно",
      });
      this.event("restart_failed", {
        trigger: "manual",
        target_version: update?.version ?? "unknown",
        error_kind: errorKind(error),
      });
      this.set({
        booting: false,
        phase: "available",
        dialog: true,
        installError: "Обновление установлено, но Airel не смог перезапуститься. Перезапустите ещё раз",
        blockingMessage: null,
      });
      throw error;
    } finally {
      releaseRestartHandoff?.();
    }
  }

  private onProgress(event: InstallProgress) {
    if (event.event === "started") {
      this.set({ phase: "downloading", downloaded: 0, contentLength: event.contentLength });
      this.event("download_started", {
        target_version: this.snapshot.update?.version ?? "unknown",
      });
      return;
    }
    if (event.event === "progress") {
      this.set({
        phase: "downloading",
        downloaded: event.downloaded,
        contentLength: event.contentLength ?? this.snapshot.contentLength,
      });
      return;
    }
    if (event.event === "verifying") {
      this.set({ phase: "verifying" });
      this.event("signature_verification_started", {
        target_version: this.snapshot.update?.version ?? "unknown",
      });
      return;
    }

    this.event("download_verified", {
      target_version: this.snapshot.update?.version ?? "unknown",
    });
  }

  /** Возвращает один cleanup даже при повторном монтаже StrictMode. */
  schedule(): () => void {
    if (!this.snapshot.desktop) return () => {};
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let onlineRetry: ReturnType<typeof setTimeout> | null = null;

    const arm = () => {
      if (!alive) return;
      const delay = PERIOD_MIN_MS + this.random() * PERIOD_JITTER_MS;
      timer = this.setTimer(() => {
        void this.check("periodic", PERIOD_MIN_MS).catch(() => {}).finally(arm);
      }, delay);
    };
    const focus = () => {
      if (document.visibilityState === "visible") void this.check("focus", FOCUS_TTL_MS).catch(() => {});
    };
    const online = () => {
      if (onlineRetry) return;
      void this.check("online", ONLINE_TTL_MS).catch(() => {
        if (!alive || onlineRetry) return;
        // Native хранит сетевой отказ 5 секунд. Один повтор после этого окна
        // покрывает online, совпавший со startup-check, и не создаёт цикл.
        onlineRetry = this.setTimer(() => {
          onlineRetry = null;
          if (alive) void this.check("online", 0).catch(() => {});
        }, ONLINE_RETRY_DELAY_MS);
      });
    };

    arm();
    document.addEventListener("visibilitychange", focus);
    window.addEventListener("online", online);

    return () => {
      alive = false;
      if (timer) this.clearTimer(timer);
      if (onlineRetry) this.clearTimer(onlineRetry);
      document.removeEventListener("visibilitychange", focus);
      window.removeEventListener("online", online);
    };
  }
}

export const updater = new UpdaterController();
